'use client'
import React, { useState, useEffect, useCallback } from 'react'

export default function EditAccount({ onSaved }) {
  // 可編輯
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [mail01, setMail01] = useState('')
  const [mail02, setMail02] = useState('')
  const [gender, setGender] = useState('') // '', '1', '2', '3'
  const [tel, setTel] = useState('')

  // 唯讀
  const [birthday, setBirthday] = useState('') // yyyy-MM-dd
  const [mobile, setMobile] = useState('')

  // UI 狀態
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // yyyy-MM-dd：優先以字串切割避免時區位移
  const fmtYMD = (val) => {
    if (!val) return ''
    if (typeof val === 'string') return val.slice(0, 10)
    try {
      const d = new Date(val)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${dd}`
    } catch {
      return ''
    }
  }

  // 取會員資料（抽成方法，更新後也會呼叫）
  const fetchMemberDetail = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/member/get_MemberDetail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const result = await res.json()
      if (!result.Error && result.Data) {
        const d = result.Data
        setFirstName(d.MemberName || '')
        setLastName(d.LastName || '')
        setMail01(d.Mail01 || '')
        setMail02(d.Mail02 || '')
        setGender(
          d.Gender === 0 || d.Gender === '0' || d.Gender == null
            ? ''
            : String(d.Gender)
        )
        setTel(d.Tel || '')
        setBirthday(fmtYMD(d.Birthday))
        setMobile(d.Mobile || '')

        onSaved?.(result)
      } else {
        console.error('get_MemberDetail error:', result?.Error)
      }
    } catch (err) {
      console.error('fetchMemberDetail error', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMemberDetail()
  }, [fetchMemberDetail])

  // 更新（不傳生日/手機）
  const handleSaveAll = async (e) => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    try {
      const payload = {
        MemberName: firstName?.trim() ?? '',
        LastName: lastName?.trim() ?? '',
        Mail01: mail01?.trim() ?? '',
        Mail02: mail02?.trim() ?? '',
        Tel: tel?.trim() ?? '',
      }
      if (gender !== '') payload.Gender = Number(gender)

      const res = await fetch('/api/member/update_Member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const result = await res.json()

      if (!result.Error) {
        alert('更新成功！')
        // ✅ 重新載入會員資料，確保畫面與資料一致
        await fetchMemberDetail()
      } else {
        alert(`更新失敗：${result.Error}`)
      }
    } catch (err) {
      console.error('handleSaveAll error', err)
      alert('更新失敗')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="col-lg-9">
        <p>載入中...</p>
      </div>
    )
  }

  return (
    <div className="col-lg-9">
      <div className="page-content my-account__edit">
        <div className="my-account__edit-form">
          <form onSubmit={handleSaveAll} className="needs-validation">
            <div className="row">
              {/* 可編輯 */}
              <div className="col-md-6">
                <div className="form-floating my-3">
                  <input
                    type="text"
                    className="form-control"
                    id="account_first_name"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <label htmlFor="account_first_name">名字 First Name</label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating my-3">
                  <input
                    type="text"
                    className="form-control"
                    id="account_last_name"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  <label htmlFor="account_last_name">姓氏 Last Name</label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating my-3">
                  <input
                    type="email"
                    className="form-control"
                    id="account_mail01"
                    placeholder="Primary Email"
                    value={mail01}
                    onChange={(e) => setMail01(e.target.value)}
                  />
                  <label htmlFor="account_mail01">
                    主要電子信箱 Primary Email
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating my-3">
                  <input
                    type="email"
                    className="form-control"
                    id="account_mail02"
                    placeholder="Secondary Email"
                    value={mail02}
                    onChange={(e) => setMail02(e.target.value)}
                  />
                  <label htmlFor="account_mail02">
                    備用電子信箱 Secondary Email
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating my-3">
                  <select
                    className="form-select"
                    id="account_gender"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="">請選擇</option>
                    <option value="1">男</option>
                    <option value="2">女</option>
                    <option value="3">其他</option>
                  </select>
                  <label htmlFor="account_gender">性別 Gender</label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating my-3">
                  <input
                    type="text"
                    className="form-control"
                    id="account_tel"
                    placeholder="Telephone"
                    value={tel}
                    onChange={(e) => setTel(e.target.value)}
                  />
                  <label htmlFor="account_tel">電話 Telephone</label>
                </div>
              </div>

              {/* 唯讀 */}
              <div className="col-md-6">
                <div className="form-floating my-3">
                  <input
                    type="text"
                    className="form-control"
                    id="account_mobile"
                    value={mobile}
                    readOnly
                  />
                  <label htmlFor="account_mobile">手機 Mobile</label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-floating my-3">
                  <input
                    type="text"
                    className="form-control"
                    id="account_birthday"
                    value={birthday}
                    readOnly
                  />
                  <label htmlFor="account_birthday">
                    生日 Birthday（yyyy-MM-dd）
                  </label>
                </div>
              </div>

              {/* 提交按鈕 */}
              <div className="col-md-12">
                <div className="my-3 d-flex gap-2">
                  <button
                    className="btn btn-primary w-100"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? '儲存中…' : '保存會員資料 Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
